import React from "react";

const sections = [
  { id: "introduction", label: "Introduction" },
  { id: "scope", label: "Scope of This Policy" },
  { id: "policy-changes", label: "Policy Updates" },
  { id: "local-law-supplements", label: "Local Law Supplements" },
  { id: "data-we-collect", label: "What Data We Collect" },
  { id: "how-we-collect", label: "How We Collect Data" },
  { id: "sms-privacy", label: "SMS Privacy" },
  { id: "push-notifications", label: "Push Notification Privacy" },  
  { id: "how-we-use", label: "How & Why We Use Your Data - Top Level" },
  { id: "how-we-use-data", label: "How & Why We Use Your Data - Table" },
  { id: "data-sharing", label: "How & Why We Share Data" },
  { id: "cookies", label: "Cookies & Tracking Technologies" },
  { id: "your-rights", label: "Your Privacy Rights & Choices" },
  { id: "security", label: "How We Protect Your Data" },
  { id: "retention", label: "How Long We Keep Your Data" },
  { id: "children", label: "Children’s Privacy" },
  { id: "state-rights", label: "US State Privacy Rights" },
  { id: "contact", label: "Contact Us" },
];

const headingStyle = {
  fontWeight: 700,
  fontSize: "1.2rem",
  marginTop: "2.5rem",
  marginBottom: "1rem",
  color: "#222",
  borderBottom: "1px solid #eee",
  paddingBottom: "0.25rem",
  letterSpacing: ".01em",
};

const subHeadingStyle = {
  fontWeight: 600,
  fontSize: "1rem",
  marginTop: "1.5rem",
  marginBottom: "0.5rem",
  color: "#333",
};

const detailLabelStyle = {
  fontWeight: 'bold',
  color: '#555',
};

const linkStyle = { color: "#0077cc", textDecoration: "underline", fontWeight: 600 };

const PrivacyPolicy: React.FC = () => (
  <div style={{ maxWidth: 900, margin: "auto", padding: "2.5rem 1rem", background: "#fff", color: "#222", fontFamily: "Segoe UI, Arial, sans-serif" }}>
    <h1 style={{ textAlign: "center", marginBottom: "1.5rem", fontWeight: 800, fontSize: "1.5rem" }}>
      Privacy Policy
    </h1>
    <p style={{ textAlign: "center", fontStyle: "italic", marginBottom: "2rem" }}>
      Effective Date: May 21, 2026
    </p>

    {/* Jump-to-section navigation */}
    <nav style={{ margin: "2rem 0 2.5rem 0", borderBottom: "1px solid #eee", paddingBottom: "1rem" }}>
      <ul style={{ listStyle: "none", padding: 0, display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "center" }}>
        {sections.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} style={{ ...linkStyle, fontSize: "1rem" }}>{s.label}</a>
          </li>
        ))}
      </ul>
    </nav>

  {/* Introduction */}
<section id="introduction">
  <h2 style={headingStyle}><b>Introduction</b></h2>
  <ul>
    <li>
      <b>Who We Are:</b> Crushhh Inc. (“Crushhh”, “we”, “us”, or “our”) is committed to protecting your privacy and ensuring you understand how your personal information is handled when you use our digital products and services, including our website (<a href="https://crushhh.com" style={linkStyle}>crushhh.com</a>), mobile applications (“Apps”), and any other platforms or features operated by Crushhh (collectively, the “Service”).
    </li>
    <li>
      <b>Purpose of This Policy:</b> This Privacy Policy explains how we collect, use, share, and protect your information, and describes your rights and choices regarding your personal data.
    </li>
    <li>
      <b>Agreement:</b> By accessing or using any part of the Service, you acknowledge and agree to the practices described in this Policy, as well as our <a href="/terms" style={linkStyle}>Terms &amp; Conditions</a> and <a href="/cookies" style={linkStyle}>Cookie Policy</a>.
    </li>
    <li>
      <b>Contact Us:</b> If you have any questions about this Policy or your data, please <a href="#contact" style={linkStyle}>contact us</a> at any time.
    </li>
  </ul>
</section>

{/* Scope of This Policy */}
<section id="scope">
  <h2 style={headingStyle}><b>Scope of This Policy</b></h2>
  <ul>
    <li>
      <b>Who Is Covered:</b> This Policy applies to all users of Crushhh’s Services in the United States.
    </li>
    <li>
      <b>International Users:</b> If you access our Services from outside the United States, additional rules or supplemental notices may apply based on your location or applicable law.
    </li>
    <li>
      <b>What Is Covered:</b> This Policy covers all features, content, and services provided by Crushhh, unless a separate privacy policy is made available for a specific product, service, or region.
    </li>
    <li>
      <b>Additional Rights:</b> For residents of California, please see our <a href="/california-privacy" style={linkStyle}>California Privacy Statement</a>, which supplements this Privacy Policy. Residents of other states or countries may also have additional rights; see our <a href="#local-law-supplements" style={linkStyle}>Local Law Supplements</a> section for more information.
    </li>
  </ul>
</section>

{/* Policy Changes & Updates */}
<section id="policy-changes">
  <h2 style={headingStyle}><b>Policy Changes & Updates</b></h2>
  <ul>
    <li>
      <b>Updates:</b> We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements.
    </li>
    <li>
      <b>Current Version:</b> The most current version will always be available at <a href="https://crushhh.com/privacy" style={linkStyle}>crushhh.com/privacy</a>.
    </li>
    <li>
      <b>Notice of Changes:</b> Material changes will be communicated to you by email (if you have an account), upon login (if you have an account), and/or by prominent notice on our website or app.
    </li>
    <li>
      <b>Your Responsibility:</b> Please review this Policy periodically to stay informed about how we protect your information.
    </li>
    <li>
      <b>Continued Use:</b> By continuing to use the Service after any changes become effective, you agree to the revised Policy. If you do not agree to the updated terms, you should discontinue use of the Service and may request account deletion.
    </li>
  </ul>
</section>

{/* Local Law Supplements */}
<section id="local-law-supplements">
  <h2 style={headingStyle}><b>Local Law Supplements</b></h2>
  <ul>
    <li>
      <b>Jurisdictional Rights:</b> Certain jurisdictions grant users additional rights or require specific disclosures.
    </li>
    <li>
      <b>Supplemental Notices:</b> Where required, we provide supplemental privacy notices or policies for residents of those jurisdictions.
    </li>
    <li>
      <b>How to Learn More:</b> Please refer to the relevant supplement for your region, or contact us if you have questions about your rights.
    </li>
  </ul>
</section>


    {/* What Data We Collect */}
    <section id="data-we-collect">
      <h2 style={headingStyle}><b>What Data We Collect</b></h2>
      <ul>
        <li><b>Account Data:</b> Email, phone number, password, and payment details. We do not retain your date of birth — it is used during age verification only and immediately discarded.</li>
        <li><b>Profile Data:</b> Preferences, social media IDs, and other profile preferences you enable.</li>
        <li><b>Verification Data:</b> Age, government ID, or other info for identity and age verification. When ID verification is handled by a third-party, we reserve the right to retain necessary information to assure age compliance.</li>
        <li><b>Usage Data:</b> Pages visited, features used, actions taken, match interactions, and engagement with content.</li>
        <li><b>Device & Technical Data:</b> Browser type, device model, OS, IP address, device identifiers, crash logs, and app settings.</li>
        <li><b>Location Data:</b> When enabled by the user, approximate or precise location (if enabled), derived from your device or IP address. Location can be disabled causing some features to be unable to be used or offered.</li>
        <li><b>Payment Data:</b> Transaction history, billing info, and payment method (handled by secure third-party processors).</li>
        <li><b>Support & Correspondence:</b> Communications with our support team, feedback, and survey responses.</li>
        <li><b>Cookies & Analytics:</b> Data collected through cookies, pixels, and analytics tools (see <a href="#cookies" style={linkStyle}>Cookies & Tracking Technologies</a>).</li>
      </ul>
      <p>
        We collect only what is necessary to provide and improve our services, protect users, and comply with legal obligations.
      </p>
    </section>

{/* SMS/Text Messaging */}
<section id="sms-privacy">
  <h2 style={headingStyle}><b>SMS/Text Messaging</b></h2>
  <ul>
    <li>
      <b>How We Use Your Phone Number:</b> When you provide your phone number to Crushhh—such as during account creation, verification, or when opting in to receive text messages—we use it to send you important notifications, account alerts, and, if you consent, promotional or marketing messages.
    </li>
    <li>
      <b>Consent and Opt-In:</b> We will only send you SMS/text messages if you have given explicit consent (opt-in) through our website, app, or other authorized means. By opting in, you agree to receive text messages from Crushhh regarding your account, matches, updates, and special offers. <b>Message frequency may vary.</b>
    </li>
    <li>
      <b>Opt-Out:</b> You may opt out of receiving SMS/text messages from us at any time by replying <b>STOP</b> to any message, or by updating your preferences in your account settings. For help, reply <b>HELP</b> or contact <a href="mailto:dpo@crushhh.com" style={{ color: "#0077cc", fontWeight: 600 }}>dpo@crushhh.com</a>.
    </li>
    <li>
      <b>Message and Data Rates:</b> Message and data rates may apply. Please check with your mobile carrier for details.
    </li>
    <li>
      <b>Data Handling:</b> We do not sell your phone number. We only share your phone number with trusted service providers as necessary to deliver SMS messages and maintain compliance with applicable laws and industry standards.
    </li>
  </ul>
  <p>
    For more information, please see our full&nbsp;
    <a href="/privacy" style={{ color: "#0077cc", fontWeight: 600 }}>Privacy Policy</a>
    &nbsp;and&nbsp;
    <a href="/terms" style={{ color: "#0077cc", fontWeight: 600 }}>Terms &amp; Conditions</a>.
  </p>
</section>

    {/* Push Notifications */}
<section id="push-notifications">
  <h2 style={headingStyle}><b>Push Notifications</b></h2>
  <ul>
    <li>
      <b>Purpose and Business Rationale:</b> Crushhh uses push notifications to deliver timely and relevant updates, such as match alerts, account activity, security notices, and important service information. Push notifications are a core part of our business model, enabling us to notify you expeditiously—even when you are not actively using the app—so you never miss a key moment or opportunity on Crushhh.
    </li>
    <li>
      <b>Consent and Opt-In:</b> We will only send you push notifications if you have provided explicit consent (opt-in) through your device or app settings. You are in control: you may choose to enable or disable push notifications at any time.
    </li>
    <li>
      <b>Feature Availability:</b> Please note that certain features or timely updates may be unavailable or limited if you choose to disable push notifications. Enabling notifications ensures you receive important alerts and the full benefit of the Crushhh experience.
    </li>
    <li>
      <b>Data Collected and Used:</b> To send push notifications, we may process device identifiers, app usage data, and notification preferences. We do not use push notifications to collect sensitive personal information, and we do not share notification data with third parties except as necessary to deliver the notifications or comply with the law.
    </li>
    <li>
      <b>User Control and Preferences:</b> You can manage your push notification preferences at any time in your device or app settings. You may opt out of receiving push notifications by disabling them in your settings, and you may adjust the types of notifications you receive to suit your preferences.
    </li>
    <li>
      <b>Transparency and Compliance:</b> We are committed to transparency regarding our use of push notifications. We comply with all applicable privacy laws and app store requirements, and we regularly review our notification practices to ensure they remain compliant and user-friendly.
    </li>
    <li>
      <b>Security and Data Minimization:</b> We use only the minimum data necessary to deliver push notifications and implement appropriate security measures to protect your information. Access to push notification data is restricted to authorized personnel only.
    </li>
    <li>
      <b>Opt-Out:</b> If you no longer wish to receive push notifications from Crushhh, you can disable them at any time through your device or app settings. For further assistance, contact <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
    </li>
  </ul>
  <p>
    For more information about our data practices, see our full <a href="/privacy" style={linkStyle}>Privacy Policy</a> and <a href="/terms" style={linkStyle}>Terms &amp; Conditions</a>.
  </p>
</section>


    {/* How We Use Your Data */}
    <section id="how-we-use">
      <h2 style={headingStyle}><b>How & Why We Use Your Data - Top Level</b></h2>
      <ul>
        <li>To create, verify, and manage your account and profile.</li>
        <li>To facilitate matching, notify you of matches, and personalize your experience.</li>
        <li>To verify your age and identity, and maintain platform safety and integrity.</li>
        <li>To process payments and handle billing or refunds.</li>
        <li>To communicate with you about updates, support, and service notifications.</li>
        <li>To analyze usage and improve features, security, and performance.</li>
        <li>To comply with legal obligations and enforce our <a href="/terms" style={linkStyle} target="_blank" rel="noopener noreferrer">Terms & Conditions</a>.</li>
      </ul>
      <p>
        We do <b>not</b> use your personal data for unrelated purposes without your consent.
      </p>


    </section>

    {/* How We Use Your Data */}
<section id="how-we-use-data">
  <h2 style={headingStyle}><b>How and Why We Use Your Data</b></h2>
  <p>
    We process your personal information to provide, maintain, and improve our services, to keep Crushhh and its users safe, and to comply with legal obligations. Below you’ll find a summary of the main purposes for which we use your data, the legal grounds for doing so, and the types of data involved. We do not use your personal data for unrelated purposes without your consent.
  </p>
    <div>
      <h3 style={subHeadingStyle}>To create, verify, and manage your account</h3>
      <p><span style={detailLabelStyle}>Legal Basis:</span> Performance of our contract with you; our legitimate interest to prevent fraud and secure our platform.</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> Contact Information (email, phone number), User-Provided Information (username), Verification Data (ID verification status, phone verification status).</p>

      <h3 style={subHeadingStyle}>To facilitate matching and personalize your experience</h3>
      <p><span style={detailLabelStyle}>Legal Basis:</span> Performance of our contract with you.</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> Contact Information of your crushes (which we hash), your list of crushes, match history.</p>
      
      <h3 style={subHeadingStyle}>To process payments and manage purchases</h3>
      <p><span>Legal Basis:</span> Performance of our contract with you.</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> Payment Information (processed by our third-party payment provider; we only receive transaction confirmation), Purchase History (e.g., membership, message credits).</p>
      
      <h3 style={subHeadingStyle}>To communicate with you</h3>
      <p><span style={detailLabelStyle}>Legal Basis:</span> Performance of our contract (e.g., match notifications), our legitimate interest (e.g., service announcements), and your consent (e.g., marketing).</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> Contact Information (email, phone number), Device Information (for push notifications).</p>
      
      <h3 style={subHeadingStyle}>To analyze usage and improve our features and security</h3>
      <p><span >Legal Basis:</span> Our legitimate interest to improve our service and ensure its security.</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> Usage Data (feature interaction, crash logs), Device Information (device type, OS version).</p>
      
      <h3 style={subHeadingStyle}>To comply with legal obligations and enforce our Terms & Conditions</h3>
      <p><span style={detailLabelStyle}>Legal Basis:</span> Compliance with a legal obligation; our legitimate interest to protect our platform and users.</p>
      <p><span style={detailLabelStyle}>Categories of Data:</span> All relevant data categories as needed to respond to legal requests or investigate violations.</p>
    </div>
  <p>
    If you have questions about how we use your data or need more detail, please contact our Data Protection Officer at <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
  </p>
</section>

    {/* How We Share Data */}
    <section id="data-sharing">
      <h2 style={headingStyle}><b>How & Why We Share Data</b></h2>
      <ul>
        <li><b>Service Providers:</b> For payment processing, hosting, analytics, security, and customer support.</li>
        <li><b>Verification Partners:</b> To confirm your age or identity, as required by law or platform policy.</li>
        <li><b>Legal Authorities:</b> If required by law, court order, or to protect rights, safety, or property.</li>
        <li><b>Other Users:</b> Only as needed for matching, and never your full contact details without your explicit consent.</li>
        <li><b>Affiliates:</b> If Crushhh is involved in a business transition (e.g., merger, acquisition), your data may be transferred as part of that process.</li>
      </ul>
      <p>
        We do <b>not</b> sell your personal information. All third parties must comply with strict confidentiality and security standards.
      </p>
    </section>

    {/* Cookies & Tracking Technologies */}
    <section id="cookies">
      <h2 style={headingStyle}><b>Cookies & Tracking Technologies</b></h2>
      <p>
        We use cookies and similar technologies to:
      </p>
      <ul>
        <li>Remember your preferences and keep you logged in.</li>
        <li>Analyze site usage and improve performance.</li>
        <li>Deliver relevant content.</li>
        <li>Protect your account and prevent fraud.</li>
      </ul>
      <p>
        For details on the types of cookies we use and your choices, please review our <a href="/cookies" style={linkStyle} target="_blank" rel="noopener noreferrer">Cookie Policy</a>.
      </p>
    </section>

{/* Your Privacy Rights & Choices */}
<section id="your-rights">
  <h2 style={headingStyle}><b>Your Privacy Rights & Choices</b></h2>
  <p>
    We want you to be in control of your personal data. Depending on your location and applicable law, you may have some or all of the rights listed below. If you have questions about your rights or how to exercise them, please contact our Data Protection Officer at <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
  </p>
    <div>
      <h3 style={subHeadingStyle}>Access / To Know / Portability</h3>
      <p><b>What It Means:</b> Request a copy of your personal data or information about how we use it.</p>
      <p><b>How to Exercise:</b> You can access and review much of your data by logging into your account. To request a copy or details, email us at <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.</p>

      <h3 style={subHeadingStyle}>Rectification / Correction</h3>
      <p><b>What It Means:</b> Ask us to fix inaccurate or incomplete information.</p>
      <p><b>How to Exercise:</b> Update your information in your account settings. For other corrections, contact us at <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.</p>

      <h3 style={subHeadingStyle}>Deletion / Erasure</h3>
      <p><b>What It Means:</b> Request deletion of your data, subject to legal or operational requirements.</p>
      <p><b>How to Exercise:</b> You may delete your data by closing your account or contacting us. We will delete or anonymize your data unless retention is required by law.</p>

      <h3 style={subHeadingStyle}>Restriction / Objection (Opt-out)</h3>
      <p><b>What It Means:</b> Limit how we use your data or object to certain uses (like marketing).</p>
      <p><b>How to Exercise:</b> Adjust your preferences in account settings or contact us to object to certain processing.</p>
      
      <h3 style={subHeadingStyle}>Consent Withdrawal</h3>
      <p><b>What It Means:</b> Withdraw consent you have given us for specific uses of your data.</p>
      <p><b>How to Exercise:</b> You can withdraw consent at any time in your settings or by contacting us. This will not affect processing already performed.</p>
    </div>
  <p>
    <b>Identity Verification:</b> To protect you and all users, we may require you to provide information to verify your identity before we fulfill your request.
  </p>
  <p>
    <b>Jurisdictional Notice:</b> The rights described above may vary depending on your country, state, or region. Please review any additional notices or supplements that apply to your location.
  </p>
</section>


    {/* How We Protect Your Data */}
    <section id="security">
      <h2 style={headingStyle}><b>How We Protect Your Data</b></h2>
      <ul>
        <li><b>Encryption:</b> All sensitive data is encrypted in transit and at rest.</li>
        <li><b>Access Controls:</b> Only authorized personnel can access your data, and all access is logged and monitored.</li>
        <li><b>Authentication:</b> Strong password requirements and optional multi-factor authentication.</li>
        <li><b>Regular Security Audits:</b> Our systems are regularly tested for vulnerabilities and compliance.</li>
        <li><b>Input Validation:</b> We validate and sanitize user input to prevent security threats.</li>
        <li><b>Backups:</b> We perform regular, secure backups to protect against data loss.</li>
      </ul>
      <p>
        While we take strong precautions, no system is 100% secure. If you have security concerns, contact <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
      </p>
    </section>

    {/* How Long We Keep Your Data */}
    <section id="retention">
      <h2 style={headingStyle}><b>How Long We Keep Your Data</b></h2>
      <ul>
        <li>We retain your personal data only as long as needed to provide our services, comply with legal obligations, and resolve disputes.</li>
        <li>When you delete your account, we remove or anonymize your data within a reasonable period, unless retention is required by law or for legitimate business purposes (e.g., fraud prevention, tax compliance).</li>
        <li>Payment and transaction data is kept as required by financial regulations.</li>
      </ul>
      <p>
        For specific retention periods or deletion requests, contact <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
      </p>
    </section>

    {/* Children's Privacy */}
    <section id="children">
      <h2 style={headingStyle}><b>Children’s Privacy</b></h2>
      <p>
        Crushhh is intended only for users age 18 and older. We do not knowingly collect or process personal data from children under 18. If we learn that a minor has registered, we will promptly delete their information.
      </p>
    </section>

    {/* US State Privacy Rights */}
    <section id="state-rights">
      <h2 style={headingStyle}><b>US State Privacy Rights</b></h2>
      <p>
        Certain US states (including California, Colorado, Connecticut, Virginia, Utah, Texas, Oregon, and others) grant residents specific rights regarding their personal information. Depending on your state, you may have the right to:
      </p>
      <ul>
        <li>Know what personal data we collect, use, and share.</li>
        <li>Access and request a copy of your data.</li>
        <li>Request deletion or correction of your data.</li>
        <li>Opt out of the sale or sharing of your data (Crushhh does not sell your data).</li>
        <li>Limit the use of sensitive personal information.</li>
        <li>Appeal decisions regarding your privacy requests.</li>
      </ul>
      <p>
        To exercise your rights or appeal a decision, email <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>. For more information, see our <a href="/terms" style={linkStyle} target="_blank" rel="noopener noreferrer">Terms & Conditions</a>.
      </p>
    </section>

    {/* Contact Us */}
    <section id="contact">
      <h2 style={headingStyle}><b>Contact Us</b></h2>
      <p>
        If you have any questions, concerns, or requests about this Privacy Policy or your data, please contact our Data Protection Officer:
      </p>
      <ul>
        <li><b>Email:</b> <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a></li>
        <li><b>Business Address:</b> CRUSHHH INC, 5830 E 2ND ST, STE 7000, CASPER, WY 82609</li>
      </ul>
    </section>
  </div>
);

export default PrivacyPolicy;
